import { useCurrencyStore, SUPPORTED_CURRENCIES } from '@/stores/currency-store';
import { fromBaseUnits, toBaseUnits } from '@/utils';
import { useExchangeRate } from './usePrices';
import { useCallback } from 'react';

export const useCurrency = () => {
	const { selectedCurrency } = useCurrencyStore();

	const suiRateQuery = useExchangeRate('0x2::sui::SUI');
	const usdcRateQuery = useExchangeRate(SUPPORTED_CURRENCIES.find((c) => c.symbol === 'USDC')?.type || '');

	const findCurrency = (type: string) => {
		return SUPPORTED_CURRENCIES.find((c) => c.type === type);
	};

	const getExchangeRate = useCallback(
		(coinType: string): number => {
			if (coinType === '0x2::sui::SUI') {
				return suiRateQuery.data || 0;
			}
			if (coinType === SUPPORTED_CURRENCIES.find((c) => c.symbol === 'USDC')?.type) {
				return usdcRateQuery.data || 0;
			}
			return 0;
		},
		[suiRateQuery.data, usdcRateQuery.data],
	);

	const convertAmount = useCallback(
		(amount: number, fromCoinType: string = '0x2::sui::SUI'): number => {
			const fromCurrency = findCurrency(fromCoinType);
			const toCurrency = findCurrency(selectedCurrency.type);

			if (!fromCurrency || !toCurrency) {
				return 0;
			}

			const decimals = fromCurrency.decimals;
			const amountInStandardUnit = fromBaseUnits(amount, decimals);

			if (fromCoinType === selectedCurrency.type) {
				return amountInStandardUnit;
			}

			const fromRate = getExchangeRate(fromCoinType);
			const toRate = getExchangeRate(selectedCurrency.type);

			if (!fromRate || !toRate || fromRate === 0 || toRate === 0) {
				return amountInStandardUnit;
			}

			const convertedAmount = amountInStandardUnit * (fromRate / toRate);

			return convertedAmount;
		},
		[selectedCurrency.type, getExchangeRate],
	);

	const formatActivityAmount = useCallback(
		(amount: number, fromCoinType: string = '0x2::sui::SUI'): string => {
			const convertedAmount = convertAmount(amount, fromCoinType);
			const decimals = convertedAmount < 1 ? 6 : convertedAmount < 100 ? 4 : 2;
			const factor = Math.pow(10, decimals);
			// Pessimistic floor to avoid over-promising
			const floored = Math.floor(convertedAmount * factor) / factor;
			return `${floored.toFixed(decimals).replace(/\.?0+$/, '')} ${selectedCurrency.symbol}`;
		},
		[convertAmount, selectedCurrency.symbol],
	);

	const formatAmount = useCallback(
		(amount: number): string => {
			const value = fromBaseUnits(amount, selectedCurrency.decimals);
			return `${value.toLocaleString()} ${selectedCurrency.symbol}`;
		},
		[selectedCurrency.decimals, selectedCurrency.symbol],
	);

	const formatStandardAmount = useCallback(
		(amount: number): string => {
			const decimals = amount < 1 ? 6 : amount < 100 ? 4 : 2;
			return `${amount.toFixed(decimals).replace(/\.?0+$/, '')} ${selectedCurrency.symbol}`;
		},
		[selectedCurrency.symbol],
	);

	return {
		selectedCurrency,
		convertAmount,
		formatAmount,
		formatStandardAmount,
		formatActivityAmount,
		toBaseUnits: (amount: number) => toBaseUnits(amount, selectedCurrency.decimals),
		fromBaseUnits: (amount: number) => fromBaseUnits(amount, selectedCurrency.decimals),
	};
};
